import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { CardChrome } from './_CardChrome';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { useExternalAuth } from '../../services/externalAuthContext';
import { ExternalApiError, mapExternalAuthError } from '../../services/api/externalAuth';
import { loginSchema, type LoginInput } from './_schemas';

interface LocationState {
  from?: { pathname?: string } | string;
  message?: string;
}

/**
 * Open-redirect defense (T-15-02-01): only honor `from` if it begins with
 * `/external/`. Anything else (including absolute URLs to other origins) is
 * rejected and we fall back to the safe default `/external/projects`
 * (Phase 23 D-05; pre-Phase-23 default was `/external/dashboard`).
 */
function safeRedirect(target: string | undefined): string {
  if (!target) return '/external/projects';
  if (typeof target !== 'string') return '/external/projects';
  if (!target.startsWith('/external/')) return '/external/projects';
  return target;
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LoginPage() {
  const { login } = useExternalAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const fromState =
    typeof state.from === 'string'
      ? state.from
      : (state.from?.pathname ?? params.get('from') ?? undefined);
  const safeFrom = safeRedirect(fromState ?? params.get('from') ?? undefined);
  const successBanner = state.message;

  const [bannerError, setBannerError] = useState<string | null>(null);
  const [lockoutSecondsRemaining, setLockoutSecondsRemaining] = useState<number | null>(null);

  // Phase 16 (D-10): MM:SS countdown lifecycle.
  // Active only when lockoutSecondsRemaining > 0; clears on unmount and on
  // every transition (a fresh successful login sets back to null below).
  useEffect(() => {
    if (lockoutSecondsRemaining === null || lockoutSecondsRemaining <= 0) return;
    const id = window.setInterval(() => {
      setLockoutSecondsRemaining((prev) => (prev === null || prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockoutSecondsRemaining]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginInput) => {
    setBannerError(null);
    try {
      await login(values);
      // Defensive (D-10): clear any stale countdown on a fresh successful login.
      setLockoutSecondsRemaining(null);
      navigate(safeFrom, { replace: true });
    } catch (err) {
      if (err instanceof ExternalApiError) {
        // Phase 16 (D-09): for 429 ACCOUNT_LOCKED, take the countdown branch
        // and BYPASS the mapper (mapper still returns the static string for
        // backwards compatibility, but this page renders MM:SS instead).
        if (err.errorCode === 'ACCOUNT_LOCKED' && typeof err.retryAfter === 'number') {
          setLockoutSecondsRemaining(err.retryAfter);
          setBannerError(null);
          return;
        }
        setBannerError(mapExternalAuthError(err, 'login'));
      } else {
        setBannerError('Something went wrong, please try again.');
      }
    }
  };

  const lockoutMessage =
    lockoutSecondsRemaining === null
      ? null
      : lockoutSecondsRemaining > 0
        ? `Too many failed attempts — try again in ${formatMmSs(lockoutSecondsRemaining)}`
        : 'You can try again now.';

  const isLockedOut = (lockoutSecondsRemaining ?? 0) > 0;

  return (
    <CardChrome>
      {successBanner && (
        <div
          role="status"
          className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 mb-4"
        >
          {successBanner}
        </div>
      )}
      {lockoutMessage && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4"
        >
          {lockoutMessage}
        </div>
      )}
      {!lockoutMessage && bannerError && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4"
        >
          {bannerError}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <div className="mt-4">
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />
        </div>
        <div className="mt-2 text-right">
          <Link
            to="/external/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Forgot password?
          </Link>
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isSubmitting}
          disabled={isLockedOut}
          className="w-full mt-6"
        >
          Sign in
        </Button>
      </form>
    </CardChrome>
  );
}
